import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Copy, AlertCircle, CheckCircle2 } from "lucide-react";

const GST_TREATMENTS = [
    { value: "registered_business_regular", label: "Registered — Regular" },
    { value: "registered_business_composition", label: "Registered — Composition" },
    { value: "unregistered_business", label: "Unregistered Business" },
    { value: "consumer", label: "Consumer (B2C)" },
    { value: "overseas", label: "Overseas (Export/Import)" },
    { value: "special_economic_zone", label: "SEZ" },
    { value: "deemed_export", label: "Deemed Export" },
    { value: "tax_deductor", label: "Tax Deductor (TDS)" },
    { value: "sez_developer", label: "SEZ Developer" },
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];

const blankAddress = () => ({ line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });

export default function PartyDrawer({ open, onClose, party, onSave, kind = "customer" }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lookupBusy, setLookupBusy] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [sameAsBilling, setSameAsBilling] = useState(true);

    useEffect(() => {
        if (!open) return;
        const initial = party ? { ...party } : {
            name: "", company: "", email: "", phone: "",
            gstin: "", pan: "", currency: "INR", payment_terms_days: 30,
            credit_limit: "", place_of_supply: "",
            gst_treatment: "registered_business_regular",
            billing_address: blankAddress(),
            shipping_address: blankAddress(),
            notes: "",
        };
        if (!initial.billing_address) initial.billing_address = blankAddress();
        if (!initial.shipping_address) initial.shipping_address = blankAddress();
        setForm(initial);
        setLookupResult(null);
        // sameAsBilling on open: if shipping matches billing, default to true
        setSameAsBilling(JSON.stringify(initial.billing_address) === JSON.stringify(initial.shipping_address));
    }, [open, party]);

    if (!form) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setAddr = (which, k, v) => setForm((f) => {
        const next = { ...f, [which]: { ...(f[which] || blankAddress()), [k]: v } };
        if (which === "billing_address" && sameAsBilling) {
            next.shipping_address = { ...next.billing_address };
        }
        return next;
    });

    const onSameAsToggle = (val) => {
        setSameAsBilling(val);
        if (val) setForm((f) => ({ ...f, shipping_address: { ...(f.billing_address || blankAddress()) } }));
    };

    const onLookupGstin = async () => {
        const g = (form.gstin || "").trim().toUpperCase();
        if (!g) return toast.error("Enter a GSTIN to lookup");
        setLookupBusy(true);
        setLookupResult(null);
        try {
            const { data } = await api.get(`/gst/lookup`, { params: { gstin: g } });
            setLookupResult(data);
            if (data.valid) {
                setForm((f) => {
                    const next = {
                        ...f,
                        gstin: data.gstin,
                        pan: f.pan || data.pan,
                        place_of_supply: f.place_of_supply || data.place_of_supply,
                        gst_treatment: f.gst_treatment || data.gst_treatment || "registered_business_regular",
                    };
                    // Auto-fill state in addresses if empty
                    if (next.billing_address && !next.billing_address.state) {
                        next.billing_address = { ...next.billing_address, state: data.state, country: "India" };
                    }
                    if (sameAsBilling) {
                        next.shipping_address = { ...next.billing_address };
                    } else if (next.shipping_address && !next.shipping_address.state) {
                        next.shipping_address = { ...next.shipping_address, state: data.state, country: "India" };
                    }
                    return next;
                });
                toast.success(`Found: ${data.holder_type} • ${data.state}`);
            } else {
                toast.error(data.reason || "Invalid GSTIN");
            }
        } catch (e) {
            toast.error("Lookup failed");
        } finally { setLookupBusy(false); }
    };

    const validate = () => {
        if (!form.name?.trim()) return "Display name is required";
        const ba = form.billing_address || {};
        if (!ba.line1 || !ba.city || !ba.state) return "Billing address (line1, city, state) is required";
        const sa = form.shipping_address || {};
        if (!sa.line1 || !sa.city || !sa.state) return "Shipping address (line1, city, state) is required";
        return null;
    };

    const submit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) return toast.error(err);
        setBusy(true);
        try {
            const payload = { ...form };
            if (payload.credit_limit === "" || payload.credit_limit == null) delete payload.credit_limit;
            await onSave(payload);
            toast.success("Saved");
            onClose();
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const isCustomer = kind === "customer";

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-[640px] p-0 flex flex-col" data-testid="party-drawer">
                <SheetHeader className="border-b border-border px-6 py-4">
                    <SheetTitle className="font-serif text-2xl font-light">
                        {party ? `Edit ${kind}` : `New ${kind}`}
                    </SheetTitle>
                </SheetHeader>
                <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Identity */}
                    <section>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">Identity</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field span={2} label="Display name" required>
                                <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="field-name" />
                            </Field>
                            <Field span={2} label="Company / legal name">
                                <Input value={form.company || ""} onChange={(e) => set("company", e.target.value)} data-testid="field-company" />
                            </Field>
                            <Field label="Email">
                                <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="field-email" />
                            </Field>
                            <Field label="Phone">
                                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="field-phone" />
                            </Field>
                        </div>
                    </section>

                    {/* GST Section */}
                    <section>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">GST & Tax Profile</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field span={2} label="GST Treatment" required>
                                <Select value={form.gst_treatment || "registered_business_regular"} onValueChange={(v) => set("gst_treatment", v)}>
                                    <SelectTrigger data-testid="field-gst-treatment"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {GST_TREATMENTS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field span={2} label="GSTIN">
                                <div className="flex gap-2">
                                    <Input
                                        value={form.gstin || ""}
                                        onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                                        placeholder="27ABCDE1234F1Z5"
                                        maxLength={15}
                                        className="font-mono uppercase"
                                        data-testid="field-gstin"
                                    />
                                    <Button type="button" variant="outline" onClick={onLookupGstin} disabled={lookupBusy} data-testid="gstin-lookup">
                                        <Wand2 className="w-4 h-4 mr-1.5" />{lookupBusy ? "…" : "Auto-fetch"}
                                    </Button>
                                </div>
                                {lookupResult && (
                                    <div className={`mt-2 text-[12px] flex items-start gap-2 ${lookupResult.valid ? "text-positive" : "text-destructive"}`}>
                                        {lookupResult.valid ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                                        <span>
                                            {lookupResult.valid
                                                ? `${lookupResult.holder_type} • ${lookupResult.state} • PAN ${lookupResult.pan}`
                                                : lookupResult.reason}
                                        </span>
                                    </div>
                                )}
                            </Field>
                            <Field label="PAN">
                                <Input value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="font-mono uppercase" data-testid="field-pan" />
                            </Field>
                            <Field label="Place of supply">
                                <Input value={form.place_of_supply || ""} onChange={(e) => set("place_of_supply", e.target.value)} data-testid="field-place-of-supply" />
                            </Field>
                        </div>
                    </section>

                    {/* Commercial */}
                    <section>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">Commercial Terms</div>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Currency">
                                <Select value={form.currency || "INR"} onValueChange={(v) => set("currency", v)}>
                                    <SelectTrigger data-testid="field-currency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Payment terms (days)">
                                <Input type="number" value={form.payment_terms_days ?? 30} onChange={(e) => set("payment_terms_days", Number(e.target.value))} data-testid="field-payment-terms" />
                            </Field>
                            {isCustomer && (
                                <Field label="Credit limit">
                                    <Input type="number" value={form.credit_limit ?? ""} onChange={(e) => set("credit_limit", e.target.value === "" ? "" : Number(e.target.value))} data-testid="field-credit-limit" />
                                </Field>
                            )}
                        </div>
                    </section>

                    {/* Billing Address */}
                    <AddressBlock
                        title="Billing Address"
                        required
                        addr={form.billing_address || blankAddress()}
                        onChange={(k, v) => setAddr("billing_address", k, v)}
                        testIdPrefix="billing"
                    />

                    {/* Shipping Address */}
                    <section>
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Shipping Address <span className="text-destructive">*</span></div>
                            <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none" data-testid="same-as-billing-toggle">
                                <input type="checkbox" checked={sameAsBilling} onChange={(e) => onSameAsToggle(e.target.checked)} />
                                <Copy className="w-3 h-3 text-muted-foreground" />
                                Same as billing
                            </label>
                        </div>
                        {!sameAsBilling && (
                            <AddressBlock
                                title=""
                                addr={form.shipping_address || blankAddress()}
                                onChange={(k, v) => setAddr("shipping_address", k, v)}
                                testIdPrefix="shipping"
                            />
                        )}
                        {sameAsBilling && (
                            <div className="border border-dashed border-border rounded-md p-4 text-[12.5px] text-muted-foreground" data-testid="shipping-mirrored">
                                Mirroring billing address. Uncheck above to enter a separate shipping address.
                            </div>
                        )}
                    </section>

                    {isCustomer && (
                        <section>
                            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Notes</Label>
                            <textarea className="mt-1.5 w-full h-20 px-3 py-2 bg-muted/40 border border-border rounded-md text-[13px]" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="field-notes" />
                        </section>
                    )}

                    <div className="flex items-center gap-2 pt-4 border-t border-border sticky bottom-0 bg-background">
                        <Button type="submit" disabled={busy} data-testid="drawer-save">{busy ? "Saving…" : "Save"}</Button>
                        <Button type="button" variant="outline" onClick={onClose} data-testid="drawer-cancel">Cancel</Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}

function Field({ label, children, span = 1, required = false }) {
    return (
        <div className={span === 2 ? "col-span-2" : "col-span-1"}>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                {label}{required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

function AddressBlock({ title, addr, onChange, required, testIdPrefix }) {
    return (
        <section>
            {title && (
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
                    {title}{required && <span className="text-destructive ml-1">*</span>}
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <Field span={2} label="Address line 1" required={required}>
                    <Input value={addr.line1 || ""} onChange={(e) => onChange("line1", e.target.value)} data-testid={`${testIdPrefix}-line1`} />
                </Field>
                <Field span={2} label="Address line 2">
                    <Input value={addr.line2 || ""} onChange={(e) => onChange("line2", e.target.value)} data-testid={`${testIdPrefix}-line2`} />
                </Field>
                <Field label="City" required={required}>
                    <Input value={addr.city || ""} onChange={(e) => onChange("city", e.target.value)} data-testid={`${testIdPrefix}-city`} />
                </Field>
                <Field label="State" required={required}>
                    <Input value={addr.state || ""} onChange={(e) => onChange("state", e.target.value)} data-testid={`${testIdPrefix}-state`} />
                </Field>
                <Field label="Pincode">
                    <Input value={addr.pincode || ""} onChange={(e) => onChange("pincode", e.target.value)} data-testid={`${testIdPrefix}-pincode`} />
                </Field>
                <Field label="Country">
                    <Input value={addr.country || ""} onChange={(e) => onChange("country", e.target.value)} data-testid={`${testIdPrefix}-country`} />
                </Field>
            </div>
        </section>
    );
}
